import { DOCUMENT } from '@angular/common';
import { inject, Injectable, signal } from '@angular/core';

export type PopupName = 'enquiry' | 'finder' | 'auth';

/** sessionStorage: the popup that has opened by itself in this visit */
const SLOT_KEY = 'lnAutoPopupSlot';
const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([type=hidden]), textarea, select, [tabindex]:not([tabindex="-1"])';

/** localStorage and sessionStorage that cannot throw (private mode, storage switched off). Browser only. */
export const storage = {
  get(kind: 'local' | 'session', key: string): string | null {
    try {
      return (kind === 'local' ? localStorage : sessionStorage).getItem(key);
    } catch {
      return null;
    }
  },
  set(kind: 'local' | 'session', key: string, value: string): void {
    try {
      (kind === 'local' ? localStorage : sessionStorage).setItem(key, value);
    } catch {
      // nothing is remembered, and nothing breaks
    }
  },
};

/**
 * Runs the page's centre popups — Enquire Now, the Recliner Finder and the
 * sign-in / sign-up popup. They share one shell (the .popup classes in
 * styles.scss): only one is open at a time, the page behind it does not
 * scroll, Tab stays inside it, Esc closes it and focus returns to whatever
 * opened it.
 *
 * It also owns the "auto-open slot": only one popup may open by itself in a
 * visit, so a first-time visitor never gets two in a row.
 */
@Injectable({ providedIn: 'root' })
export class Popups {
  /** the popup on screen, if any */
  readonly open = signal<PopupName | null>(null);
  /** the Recliner Finder's answers, which "Talk to an expert" hands to the enquiry form's Message field */
  readonly note = signal('');
  /** a coupon was claimed in this visit: the Enquire Now button has done its job and goes away */
  readonly claimed = signal(false);
  /** the name of the account that is signed in, for the header's Login button — null when nobody is */
  readonly account = signal<string | null>(null);

  private readonly document = inject(DOCUMENT);
  /** what had focus before a popup opened: it gets focus back */
  private opener: Element | null = null;
  private listening = false;

  show(name: PopupName): void {
    if (this.open() === name) return;
    // (one popup handing over to the other keeps the first one's opener)
    if (!this.open()) this.opener = this.document.activeElement;
    this.open.set(name);
    this.document.body.classList.add('popup-lock');
    if (!this.listening) {
      this.listening = true;
      this.document.addEventListener('keydown', (event) => this.onKey(event));
    }
    // once it is on screen: into the control it marks for this, or failing that its first one
    setTimeout(() => {
      const marked = this.dialog()?.querySelector<HTMLElement>('[data-popup-focus]');
      (marked ?? this.focusables()[0])?.focus();
    }, 60);
  }

  close(): void {
    if (!this.open()) return;
    this.open.set(null);
    this.document.body.classList.remove('popup-lock');
    (this.opener as HTMLElement | null)?.focus?.();
    this.opener = null;
  }

  /**
   * Lets a popup open by itself: after `delay` ms, or once the visitor has
   * scrolled `depth` of the way down the page, whichever comes first — but
   * never over another popup, and only if no popup has done so in this visit.
   * Browser only: call it from afterNextRender.
   */
  autoOpen(name: PopupName, delay: number, depth: number): void {
    if (storage.get('session', SLOT_KEY)) return;

    let done = false;
    const fire = () => {
      if (done || this.open()) return;
      const holder = storage.get('session', SLOT_KEY);
      if (holder && holder !== name) return;
      storage.set('session', SLOT_KEY, name);
      done = true;
      this.show(name);
    };
    const onScroll = () => {
      const room = this.document.body.scrollHeight - window.innerHeight;
      if (room > 0 && window.scrollY / room > depth) {
        window.removeEventListener('scroll', onScroll);
        fire();
      }
    };
    setTimeout(fire, delay);
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  private dialog(): HTMLElement | null {
    return this.document.querySelector<HTMLElement>('.popup.is-open');
  }

  /** the controls of the open popup that Tab can reach */
  private focusables(): HTMLElement[] {
    const all = this.dialog()?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? [];
    return Array.from(all).filter((el) => el.offsetWidth > 0 || el.offsetHeight > 0);
  }

  private onKey(event: KeyboardEvent): void {
    if (!this.open()) return;
    if (event.key === 'Escape') {
      this.close();
      return;
    }
    if (event.key !== 'Tab') return;

    const all = this.focusables();
    if (!all.length) return;
    const first = all[0];
    const last = all[all.length - 1];
    const active = this.document.activeElement;
    // Tab off either end wraps round — as it does when focus has slipped out of
    // the popup altogether (the button it was on is no longer there)
    const outside = !this.dialog()?.contains(active);
    if (event.shiftKey ? active === first || outside : active === last || outside) {
      event.preventDefault();
      (event.shiftKey ? last : first).focus();
    }
  }
}
